import VueRouter, { Route } from 'vue-router';
import { IModal, modalService } from '@gig/magic-common';
import {
    CLOUDFLARE_QUERY,
    Feature,
    FEATURE_QUERY_ACTIVE,
    FEATURE_QUERY_VALUE,
    IFeatureQuery,
    IFeatureValue,
} from '@gig/magic-messages';
import { RouteName } from '../../../routes/routes.constants';

export function initAllowedCountries(router: VueRouter) {
    let modalRef: IModal | null = null;
    router.beforeEach(async (to, from, next) => {
        if (await isLocationAllowedForRoute(to)) {
            if (modalRef) {
                modalService.close(modalRef);
                modalRef = null;
            }
            return next();
        }

        if (isAllowedCountriesModalOpen()) {
            return next(
                new Error(
                    'Navigation canceled due to user block (allowed countries)',
                ),
            );
        }

        modalRef = setupAllowedCountriesModal();
        return next(
            from.fullPath
                ? {
                      path: from.fullPath,
                  }
                : {
                      name: RouteName.HOME,
                  },
        );
    });
}

export function allowedCountriesGuard() {
    return {
        allowedCountries: true,
    };
}

export async function isLocationAllowedForRoute(to: Route) {
    if (!getAllowedCountries(to)) {
        return true;
    }

    const isFeatureEnabled = await window.queryBus.query<string, boolean>(
        FEATURE_QUERY_ACTIVE,
        Feature.ALLOWED_COUNTRIES,
    );

    if (!isFeatureEnabled) {
        return true;
    }

    const featureSetting = (
        await window.queryBus.query<IFeatureQuery, IFeatureValue>(
            FEATURE_QUERY_VALUE,
            {
                slug: Feature.ALLOWED_COUNTRIES,
                defaultValue: [],
            },
        )
    )?.value;

    const allowedIp: string[] =
        featureSetting?.length > 0
            ? featureSetting.split(',').map((item: string) => item.trim())
            : [];

    const ip = await getCloudflareQuery('ip');

    if (allowedIp.includes(ip)) {
        return true;
    }

    const location = await getCloudflareQuery('loc');
    const { isApplicable } = await window.queryBus.query<
        IFeatureQuery,
        IFeatureValue
    >(FEATURE_QUERY_VALUE, { slug: Feature.ALLOWED_COUNTRIES, location });

    return isApplicable;
}

function getAllowedCountries(route: Route): boolean {
    const matchedRoute = route.matched.find((currRoute) => {
        return currRoute.meta?.allowedCountries;
    });
    return matchedRoute?.meta?.allowedCountries ?? false;
}

export function setupAllowedCountriesModal() {
    const modal = document.createElement('magic-allowed-countries-modal');
    const header = 'allowed-countries-modal.title';
    const modalOptions = {
        dismissible: true,
        dismissibleBackdrop: true,
        displayOnTop: true,
    };
    return modalService.open(header, modal, modalOptions);
}

function getCloudflareQuery(property: string) {
    return window.queryBus.query<string, string>(CLOUDFLARE_QUERY, property);
}

function isAllowedCountriesModalOpen() {
    return (
        document.getElementsByTagName('magic-allowed-countries-modal').length !=
        0
    );
}
